<template>
  <modal
    name="pickStory"
    :adaptive="true"
    height="auto"
    width="70%"
    :maxWidth="1200"
    :scrollable="true"
    @before-open="beforeOpen"
    @before-close="beforeClose"
    class="z-100"
    ref="modal"
  >
    <div class="text-2xl font-medium text-center py-2 bg-blue-500 text-white" v-html="$sanitize(message)"></div>
    <div class="flex flex-col rounded-lg justify-center text-gray-600">
      <!-- <div class="bg-blue-500 text-center text-lg font-bold text-white p-2">Choose a story</div> -->
      <div class="p-4">
        <div class="justify-center pt-2 items-center gap-2 flex-wrap">
          <div class="flex items-center space-x-2 justify-center mb-4">
            <div class="date-picker-container">
              <date-picker v-model="selectedDate" />
            </div>
            <div class="text-sm text-gray-500">Selected date includes a 3 month range</div>
          </div>
          <!-- <div class="text-xl font-semibold">Story</div> -->
          <div class="flex items-center space-x-2">
            <div class="text-xl font-semibold">Story</div>
            <multiselect
              v-model="selectedStory"
              id="ajax"
              label="title"
              track-by="id"
              placeholder="Type to search"
              open-direction="bottom"
              :options="storyOptions"
              :searchable="true"
              :show-labels="false"
              :loading="isLoading"
              :internal-search="false"
              :options-limit="300"
              :limit="3"
              :max-height="600"
              :show-no-results="true"
              @search-change="searchChange($event)"
            >
              <template v-slot:singleLabel="{ option }">
                <div class="flex justify-between">
                  <div>{{ option.title }}</div>
                  <div>{{ option.story_date }}</div>
                </div>
              </template>
              <template v-slot:noResult>
                <div class="flex justify-between">No Stories Found.</div>
              </template>
              <template v-slot:option="props">
                <div class="flex justify-between">
                  <span class="font-semibold">{{ props.option.title }}</span>
                  <div class="flex gap-2 items-center">
                    <!-- ICONS -->
                    <div class="flex gap-1 opacity-50">
                      <div class="flex items-center">
                        <message-circle-icon class="w-4 mr-1" />
                        <div>{{ props.option.comment_count }}</div>
                      </div>
                      <div class="flex items-center">
                        <paperclip-icon class="w-4 mr-1" />
                        <div>{{ props.option.attachment_count }}</div>
                      </div>
                      <div class="flex items-center">
                        <film-icon class="w-4 mr-1" />
                        <div>{{ props.option.media_asset_count }}</div>
                      </div>
                      <div class="flex items-center">
                        <check-square-icon class="w-4 mr-1" />
                        <div>{{ props.option.task_count }}</div>
                      </div>
                    </div>
                    <span class="">{{ props.option.story_date }}</span>
                  </div>
                </div>
              </template>
            </multiselect>
          </div>
        </div>
        <div v-if="selectedStory" class="flex w-full items-stretch mt-4">
          <story-card v-if="story" class="flex-1" :story="story" />
          <div v-if="story" class="flex justify-center px-3" @click="mergeLeft = !mergeLeft">
            <chevron-right-icon
              class="transition flex-shrink-0 my-auto rounded bg-blue-500 text-white cursor-pointer"
              :class="mergeLeft ? 'transform rotate-180' : ''"
            />
          </div>
          <story-card class="flex-1 border" :story="selectedStory" />
        </div>
        <div class="flex justify-end pt-4 items-center gap-4">
          <form-button type="button" @click="pickStory" :spin="saving">{{ submitLabel ? submitLabel : 'Select' }}</form-button>
          <form-button type="button" @click="cancelModal" classes="bg-red-500">{{ cancelLabel ? cancelLabel : 'Cancel' }}</form-button>
        </div>
      </div>
    </div>
  </modal>
</template>
<script>
import api from '@/api'
import { mapGetters, mapActions } from 'vuex'
import StoryCard from '@/components/story/StoryCard.vue'
import { DatePicker, FeatherIcons } from '@/scripps-components'
import Multiselect from 'vue-multiselect'
import _ from 'lodash'

import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone.js'
dayjs.extend(timezone)
const { ChevronRightIcon, CheckSquareIcon, FilmIcon, MessageCircleIcon, PaperclipIcon } = FeatherIcons

export default {
  components: { ChevronRightIcon, StoryCard, Multiselect, CheckSquareIcon, FilmIcon, MessageCircleIcon, PaperclipIcon, DatePicker },
  data() {
    return {
      saving: false,
      storyOptions: [],
      baseOptions: [],
      mergeLeft: false,
      story: null,
      selectedStory: null,
      message: null,
      submitLabel: null,
      cancelLabel: null,
      title: null,
      showDates: false,
      isLoading: false,
      submitCallback: () => {},
      completeCallback: () => {},
      selectedDate: '',
      lastSearchQuery: ''
    }
  },
  watch: {
    selectedDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        // Clear story options and fetch updated results based on the new date
        this.storyOptions = []
        this.searchChange(this.lastSearchQuery, newVal)
      }
    }
  },
  mounted() {
    window.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    window.removeEventListener('click', this.handleClickOutside)
  },
  props: {},
  computed: {
    ...mapGetters(['stories', 'currentStation', 'currentTimezone']),
    options() {
      let options = []
      let today = dayjs().tz(this.currentTimezone).format('YYYY-MM-DD')
      this.storyOptions.forEach((s) => {
        // if (today == s.story_date && s.story_approved == 'Approved')
        let label = s.title
        if (this.showDates) label = `[${s.story_date ?? 'TBD'}] ${s.title}`
        options.push({ value: s.id, label })
      })
      options.sort((a, b) => {
        if (!this.title) return 0
        const levA = this.$utils.levenshtein.distance(a.label.toLowerCase(), this.title.toLowerCase())
        const levB = this.$utils.levenshtein.distance(b.label.toLowerCase(), this.title.toLowerCase())
        return levA - levB
      })
      return options
    }
  },
  async created() {
    await this.getChanges()
  },
  methods: {
    ...mapActions(['getChanges']),
    searchChange: _.debounce(async function (query) {
      this.isLoading = true
      try {
        // Define start and end dates (three months range) based on the selected date
        let startDate = dayjs(this.selectedDate).subtract(3, 'month').format('YYYY-MM-DD')
        let endDate = dayjs(this.selectedDate).add(3, 'month').format('YYYY-MM-DD')
        let params = {
          stations: [this.story?.station ?? this.currentStation.station],
          title: query,
          startDate,
          endDate,
          include: ''
        }
        const resp = await api.newsdesk.globalSearch(params, 30, true)
        // Update the list of available stories based on the search and refresh the UI
        this.storyOptions = [...resp.data]

      } catch (error) {
        console.error('Error fetching stories:', error)
        this.$alerts.$alert('Error searching', error.message, 'error')
      } finally {
        this.isLoading = false
      }
    }, 500),
    async pickStory() {
      this.saving = true
      if (!this.selectedStory) {
        this.saving = false
        return this.$alerts.$alert('No story selected', 'Error', 'error')
      }
      let story = this.selectedStory ?? null
      if (!story) {
        this.saving = false
        return this.$alerts.$alert('Story not found', this.selectedStory?.id, 'error')
      }
      let result = await this.submitCallback(story, this.mergeLeft)
      this.cancelModal()
      this.saving = false
    },
    cancelModal() {
      this.$modal.hide('pickStory')
    },
    beforeOpen({ params, cancel }) {
      if (!params) {
        this.$alerts.$alert('Missing Params', 'Error', 'error')
        return cancel()
      }
      if (!params.submitCallback) {
        cancel()
        this.$alerts.$alert('Submit function is missing', 'Error', 'error')
        return
      }
      this.storyOptions = params.stories ?? this.stories
      this.baseOptions = _.cloneDeep(this.storyOptions ?? [])
      this.story = params.story

      this.submitCallback = params.submitCallback
      this.completeCallback = params.completeCallback
      this.message = params.message
      this.title = params.title
      this.submitLabel = params.submitLabel
      this.cancelLabel = params.cancelLabel
      this.showDates = params.showDates ?? false
      this.selectedStory = this.storyOptions[0] ?? null
      this.selectedDate = this.getDefaultDateToDefault() // set to today's date
    },
    async beforeClose() {
      this.selectedStory = null
      this.callback = () => {}
      this.message = null
      this.title = null
      this.submitLabel = null
      this.cancelLabel = null
      this.saving = false
      await this.completeCallback()
      this.completeCallback = () => {}
      this.submitCallback = () => {}
    },
    getDefaultDateToDefault() {
      return dayjs().tz(this.currentTimezone).format('YYYY-MM-DD') // set to today's date
    }
  }
}
</script>
<style lang="postcss" scoped></style>
